// src/business/components/product/steps/StepVariants.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import VariantMatrix from "../VariantMatrix";
import {
  buildOptionCombinations,
  makeVariantKey,
  reconcileVariantsWithOptions,
} from "../utils/variantMatrix";

function sameVariantState(a = [], b = []) {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i += 1) {
    const left = a[i] || {};
    const right = b[i] || {};
    if (makeVariantKey(left.optionValues) !== makeVariantKey(right.optionValues)) return false;
    if (String(left.sku || "") !== String(right.sku || "")) return false;
    if (String(left.price ?? "") !== String(right.price ?? "")) return false;
    if (Boolean(left.isActive !== false) !== Boolean(right.isActive !== false)) return false;
  }

  return true;
}

export default function StepVariants({ state, setState, saveWarnings = [] }) {
  const combos = useMemo(() => buildOptionCombinations(state.options), [state.options]);
  const expectedVariants = useMemo(
    () => reconcileVariantsWithOptions(state.variants || [], state.options || []),
    [state.options, state.variants]
  );

  const hasNoCombos = combos.length === 0;

  const [expandedHelp, setExpandedHelp] = useState(false);

  const skuConflictWarnings = useMemo(
    () =>
      (Array.isArray(saveWarnings) ? saveWarnings : []).filter((warning) =>
        /^SKU\s+"/i.test(String(warning || "").trim())
      ),
    [saveWarnings]
  );

  const conflictingSkus = useMemo(() => {
    const skus = new Set();
    skuConflictWarnings.forEach((warning) => {
      const match = String(warning).match(/^SKU\s+"([^"]+)"/i);
      if (match?.[1]) skus.add(match[1].trim().toUpperCase());
    });
    return skus;
  }, [skuConflictWarnings]);

  const reviewSkuConflicts = () => {
    window.requestAnimationFrame(() => {
      document
        .querySelector('[data-sku-conflict="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const setVariants = useCallback((fnOrArray) => {
    setState((p) => ({
      ...p,
      variants: typeof fnOrArray === "function" ? fnOrArray(p.variants || []) : fnOrArray,
    }));
  }, [setState]);

  useEffect(() => {
    setVariants((prev) => {
      const next = reconcileVariantsWithOptions(prev || [], state.options || []);
      return sameVariantState(prev || [], next) ? prev : next;
    });
  }, [setVariants, state.options, state.variants]);

  const issues = useMemo(() => {
    const out = [];
    if (hasNoCombos) out.push("Add option values first to generate combinations.");
    if (!hasNoCombos && (state.variants || []).length === 0) {
      out.push("Variants are generated automatically from options.");
    }
    return out;
  }, [hasNoCombos, state.variants]);

  const ok =
    skuConflictWarnings.length === 0 &&
    !hasNoCombos &&
    (state.variants || []).length > 0 &&
    sameVariantState(state.variants || [], expectedVariants);

  return (
    <div className="space-y-3">
      {/* Status card */}
      <div
        className={
          "rounded-2xl border border-l-4 bg-white px-4 py-3 shadow-sm dark:bg-[#121214] " +
          (ok
            ? "border-zinc-200 border-l-emerald-500 dark:border-zinc-800 dark:border-l-emerald-500"
            : "border-zinc-200 border-l-amber-500 dark:border-zinc-800 dark:border-l-amber-500")
        }
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Variants
            </div>
            <div className="mt-1 max-w-4xl text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
              Variant rows are generated automatically from your option combinations. Manage SKU, pricing, and activation here. Variant image assignment now lives in Media.
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <div className="min-w-[76px] rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-center dark:border-zinc-800 dark:bg-zinc-900/50">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Combos</div>
              <div className="mt-0.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{combos.length}</div>
            </div>
            <div className="min-w-[96px] rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-center dark:border-zinc-800 dark:bg-zinc-900/50">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Variants</div>
              <div className="mt-0.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{(state.variants || []).length}</div>
            </div>
            <span
            className={
              "inline-flex w-fit shrink-0 items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold " +
              (ok
                ? "border-emerald-200 bg-white text-emerald-800 dark:border-emerald-900/40 dark:bg-transparent dark:text-emerald-200"
                : "border-amber-200 bg-white text-amber-800 dark:border-amber-900/40 dark:bg-transparent dark:text-amber-200")
            }
          >
            {ok ? "Complete" : "Needs attention"}
            </span>
          </div>
        </div>

        <div className="hidden">
          Combos:{" "}
          <span className="font-semibold text-zinc-900 dark:text-zinc-100">{combos.length}</span>
          <span className="mx-2">•</span>
          Current variants:{" "}
          <span className="font-semibold text-zinc-900 dark:text-zinc-100">
            {(state.variants || []).length}
          </span>
        </div>

        {!ok && issues.length > 0 && (
          <div className="mt-3 text-xs text-amber-900 dark:text-amber-100">
            <ul className="list-disc pl-5 space-y-1">
              {issues.slice(0, 4).map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          onClick={() => setExpandedHelp((v) => !v)}
          className="mt-3 inline-flex items-center rounded-lg border border-zinc-200/80 bg-white/70 px-2.5 py-1.5 text-xs font-medium text-zinc-600 transition hover:bg-white dark:border-zinc-700 dark:bg-black/10 dark:text-zinc-300 dark:hover:bg-black/20"
        >
          {expandedHelp ? "Hide" : "How generation works"}
        </button>

        {expandedHelp && (
          <div className="mt-3 rounded-xl border border-zinc-200 bg-white/80 p-3 text-xs leading-relaxed text-zinc-600
                          dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-400">
            <ul className="grid gap-2 sm:grid-cols-2">
              <li className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-zinc-900/60">Every option combination gets one variant row automatically.</li>
              <li className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-zinc-900/60">Adding option values creates new rows automatically.</li>
              <li className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-zinc-900/60">Removing option values removes orphaned rows automatically.</li>
              <li className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-zinc-900/60">Existing matching rows keep their edited SKU, price, and active status.</li>
            </ul>
          </div>
        )}
      </div>

      {skuConflictWarnings.length > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm dark:border-amber-800/70 dark:bg-amber-950/30 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold">
              {skuConflictWarnings.length} SKU conflict{skuConflictWarnings.length === 1 ? " needs" : "s need"} attention
            </div>
            <p className="mt-1 text-xs leading-relaxed text-amber-800 dark:text-amber-200">
              Your draft was saved. Change each highlighted SKU before listing the product for sale.
            </p>
          </div>
          <button
            type="button"
            onClick={reviewSkuConflicts}
            className="inline-flex shrink-0 items-center justify-center rounded-xl border border-amber-500 bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-950 transition hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/60 dark:text-amber-100 dark:hover:bg-amber-900"
          >
            Review conflicts
          </button>
        </div>
      )}

      {hasNoCombos && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600
                        dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-400">
          Add option values first (e.g., Size and Color) to generate variant combinations.
        </div>
      )}

      {/* Old admin behavior: VariantMatrix lives here */}
      <VariantMatrix
        productName={state.basic?.name || ""}
        skuBase={state.basic?.skuBase || ""}
        setSkuBase={(value) =>
          setState((p) => ({
            ...p,
            basic: { ...(p.basic || {}), skuBase: value },
          }))
        }
        variants={state.variants || []}
        setVariants={setVariants}
        options={state.options || []}
        conflictingSkus={conflictingSkus}
      />
    </div>
  );
}
